import React, { useEffect, useState } from "react";
import { CheckCircle, Warning, ArrowUUpLeft } from "@phosphor-icons/react";
import { useI18n } from "../lib/i18n";

const pad = (n) => String(n).padStart(2, "0");

// Full-screen break timer. Employees cannot end a break until the required
// time has elapsed; an Accidental Start escape cancels a mistaken break.
export default function BreakTimer({ kind, start, durationMinutes, onEnd, onAccidental, busy }) {
  const { t } = useI18n();
  const startMs = new Date(start).getTime();
  const requiredSec = Math.max(1, durationMinutes * 60);
  const [now, setNow] = useState(Date.now());
  const [confirmAcc, setConfirmAcc] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const elapsedSec = Math.floor((now - startMs) / 1000);
  const remainingSec = Math.max(0, requiredSec - elapsedSec);
  const complete = remainingSec <= 0;
  const pct = Math.min(100, Math.round((elapsedSec / requiredSec) * 100));
  const mm = Math.floor(remainingSec / 60);
  const ss = remainingSec % 60;

  const isMeal = kind === "meal";
  const bg = isMeal ? "#1F3D34" : "#3B2F1E";
  const accent = isMeal ? "#7C9673" : "#D8C7A1";
  const returnTime = new Date(startMs + requiredSec * 1000);

  return (
    <div
      data-testid="break-timer-overlay"
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center text-white px-6 py-8 text-center"
      style={{ backgroundColor: bg }}
    >
      <div className="text-sm font-bold uppercase tracking-[0.3em]" style={{ color: accent }}>
        {isMeal ? t("bt.meal") : t("bt.rest")}
      </div>

      <div
        data-testid="break-countdown"
        className="font-mono font-black leading-none my-6"
        style={{ fontSize: "clamp(5rem, 22vw, 12rem)" }}
      >
        {pad(mm)}:{pad(ss)}
      </div>

      <div className="w-full max-w-md">
        <div className="h-3 bg-white/15 rounded-full overflow-hidden">
          <div data-testid="break-progress" className="h-3 rounded-full transition-all duration-1000" style={{ width: `${pct}%`, backgroundColor: accent }} />
        </div>
        <div className="text-xs font-bold uppercase tracking-wide mt-2 opacity-80">{pct}% {t("bt.complete")}</div>
      </div>

      <div className="grid grid-cols-3 gap-4 mt-8 text-xs uppercase tracking-wide opacity-90 max-w-md w-full">
        <div><div className="opacity-60">{t("bt.started")}</div><div className="font-mono text-sm mt-0.5">{new Date(startMs).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div></div>
        <div><div className="opacity-60">{t("bt.required")}</div><div className="font-mono text-sm mt-0.5">{durationMinutes} {t("bt.min")}</div></div>
        <div><div className="opacity-60">{t("bt.return_at")}</div><div className="font-mono text-sm mt-0.5">{returnTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div></div>
      </div>

      <p className="max-w-md text-sm mt-8 leading-relaxed opacity-90" data-testid="break-reminder">
        {complete ? t("bt.done_msg") : t("bt.reminder")}
      </p>

      {!complete && (
        <div className="mt-4 flex items-center gap-2 text-sm font-bold" style={{ color: accent }} data-testid="break-incomplete-msg">
          <Warning weight="fill" /> {t("bt.incomplete")}
        </div>
      )}

      <button
        data-testid="break-end-btn"
        disabled={!complete || busy}
        onClick={onEnd}
        className="mt-8 w-full max-w-md h-20 font-black uppercase text-xl flex items-center justify-center gap-3 border-2 disabled:opacity-30 disabled:cursor-not-allowed"
        style={{ backgroundColor: complete ? accent : "transparent", color: complete ? bg : "white", borderColor: accent }}
      >
        <CheckCircle weight="fill" size={26} /> {t("bt.end_break")}
      </button>

      {!confirmAcc ? (
        <button
          data-testid="accidental-start-btn"
          onClick={() => setConfirmAcc(true)}
          className="mt-4 text-xs uppercase tracking-wide underline opacity-70 flex items-center gap-1.5"
        >
          <ArrowUUpLeft size={14} /> {t("bt.accidental")}
        </button>
      ) : (
        <div className="mt-4 bg-white/10 border border-white/20 p-4 max-w-md w-full" data-testid="accidental-confirm">
          <div className="text-sm font-bold mb-3">{t("bt.accidental_q")}</div>
          <div className="flex gap-3">
            <button data-testid="accidental-yes" disabled={busy} onClick={onAccidental} className="flex-1 h-12 bg-white text-stone-900 font-bold uppercase text-sm">{t("bt.yes_cancel")}</button>
            <button data-testid="accidental-no" onClick={() => setConfirmAcc(false)} className="flex-1 h-12 border border-white/40 font-bold uppercase text-sm">{t("bt.no")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
